import {
  Container,
  EmptyState,
  Flex,
  Heading,
  Table,
  VStack,
  Button,
  Link,
  Box,
} from "@chakra-ui/react";
import { FiSearch } from "react-icons/fi";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  TablesService,
  TableRowsService,
  TableFieldsService,
  type OpenSearchDoc,
} from "@/client";
import type { ApiError } from "@/client/core/ApiError";
import { ActionsMenu } from "@/components/Common/ActionsMenu";
import PendingItems from "@/components/Pending/PendingItems";
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination";
import { ProcessStateTag } from "@/components/ui/state-tag";
import AddTableRow from "@/components/TableRows/AddTableRow";
import AddTableRowFromSchema from "@/components/TableRows/AddTableRowFromSchema";
import BulkUploadTableRows from "@/components/TableRows/BulkUploadTableRows";
import { format, parseISO } from "date-fns";
import type { ReactNode } from "react";
import { useState } from "react";

const tableSearchSchema = z.object({
  page: z.number().catch(1),
});

const PER_PAGE = 5;

async function fetchTables() {
  return await TablesService.readTables();
}

function readTableRowsQueryOptions({
  tableId,
  page,
}: {
  tableId: string;
  page: number;
}) {
  return {
    queryFn: async () => {
      const result = await TableRowsService.readTableRows({
        tableId,
        skip: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
      });
      return result;
    },
    queryKey: ["openSearchDocs", { tableId, page }],
  };
}

export const Route = createFileRoute("/_layout/table/$tableId")({
  component: TableRows,
  validateSearch: (search) => tableSearchSchema.parse(search),
});

function TableDetail({ hasFields }: { hasFields: boolean }) {
  const navigate = useNavigate({ from: Route.fullPath });
  const { page } = Route.useSearch();
  const { tableId } = Route.useParams();
  const [_dialogOpen, setDialogOpen] = useState(false);
  const [_selectedDoc, setSelectedDoc] = useState<OpenSearchDoc | null>(null);

  function handleRowClick(doc: OpenSearchDoc) {
    setSelectedDoc(doc);
    setDialogOpen(true);
  }

  const { data, isLoading, isPlaceholderData, error } = useQuery({
    ...readTableRowsQueryOptions({ tableId, page }),
    enabled: hasFields,
    // placeholderData: (prev) => prev,
    retry: 1,
    retryDelay: 1000,
  });

  // 에러 로깅 추가
  if (error) {
    console.error("테이블 행 데이터 로딩 에러:", error);
    const apiError = error as ApiError;
    console.error("에러 상세 정보:", {
      message: apiError.message,
      status: apiError.status,
    });
  }

  const setPage = (page: number) =>
    navigate({
      search: (prev: { page: number }) => ({ ...prev, page }),
    });

  if (isLoading) return <PendingItems />;

  const fields = data?.fields ?? [];
  const openSearchDocs = data?.openSearchDocs ?? [];
  const count = data?.count ?? 0;

  function getContrastColor(bgColor: string) {
    // Simple luminance-based contrast check
    const color = bgColor.charAt(0) === "#" ? bgColor.substring(1, 7) : bgColor;
    const r = Number.parseInt(color.substring(0, 2), 16);
    const g = Number.parseInt(color.substring(2, 4), 16);
    const b = Number.parseInt(color.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.6 ? "#000000" : "#FFFFFF";
  }

  return (
    <>
      {/* <Heading size="md" mb={4}>
        {data?.table_name} - {data?.description}
      </Heading> */}

      <Box maxH="70vh" overflowY="auto">
        <Table.Root size={{ base: "sm", md: "md" }}>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader width="100px" maxW="100px">
                ID
              </Table.ColumnHeader>
              {fields.map((field) => (
                <Table.ColumnHeader key={`header-${field.field_id}`}>
                  {String(field.field_name)}
                </Table.ColumnHeader>
              ))}
              <Table.ColumnHeader key="created_at">생성일</Table.ColumnHeader>
              <Table.ColumnHeader minW="60px">상태</Table.ColumnHeader>
              <Table.ColumnHeader>작업</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {fields.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={3}>
                  <EmptyState.Root>
                    <EmptyState.Content>
                      <EmptyState.Indicator>
                        <FiSearch />
                      </EmptyState.Indicator>
                      <VStack textAlign="center">
                        <EmptyState.Title>
                          정의된 데이블 필드가 없습니다.
                        </EmptyState.Title>
                        <EmptyState.Description>
                          데이터를 추가하려면 먼저 필드를 추가하세요.
                        </EmptyState.Description>
                      </VStack>
                    </EmptyState.Content>
                  </EmptyState.Root>
                </Table.Cell>
              </Table.Row>
            ) : count === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={fields.length + 3}>
                  <EmptyState.Root>
                    <EmptyState.Content>
                      <EmptyState.Indicator>
                        <FiSearch />
                      </EmptyState.Indicator>
                      <VStack textAlign="center">
                        <EmptyState.Title>No rows yet</EmptyState.Title>
                        <EmptyState.Description>
                          이 테이블은 데이터가 없습니다.
                        </EmptyState.Description>
                      </VStack>
                    </EmptyState.Content>
                  </EmptyState.Root>
                </Table.Cell>
              </Table.Row>
            ) : (
              openSearchDocs.map((openSearchDoc) => (
                <Table.Row
                  key={openSearchDoc.id}
                  onClick={() => handleRowClick(openSearchDoc)}
                  cursor="pointer"
                  _hover={{ bg: "gray.50" }}
                  opacity={isPlaceholderData ? 0.5 : 1}
                >
                  <Table.Cell
                    width="100px"
                    maxW="100px"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {openSearchDoc.id}
                  </Table.Cell>
                  {fields.map((field) => (
                    <Table.Cell
                      key={`cell-${openSearchDoc.id}-${field.field_id}`}
                      // maxH="4.5em"
                      width="400px"
                      maxW="400px"
                      truncate
                      // lineClamp="2"
                      // overflow="hidden"
                      // textOverflow="ellipsis"
                    >
                      {(() => {
                        const rawValue = (
                          openSearchDoc.data as Record<string, unknown>
                        )?.[field.field_name as string] as ReactNode;

                        // N/A 값 처리
                        if (!rawValue || rawValue === "N/A") return "-";

                        const type = field.datetime_type ?? field.field_type;

                        try {
                          if (type === "datetime") {
                            return format(
                              parseISO(rawValue as string),
                              "yyyy/MM/dd HH:mm"
                            );
                          }
                          if (type === "date") {
                            return format(
                              parseISO(rawValue as string),
                              "yyyy/MM/dd"
                            );
                          }
                          if (type === "time") {
                            return format(
                              parseISO(rawValue as string),
                              "HH:mm"
                            );
                          }
                          if (type === "selection" && field.options) {
                            const matched = (
                              field.options as Array<{
                                label: string;
                                color: string;
                              }>
                            ).find((opt) => opt.label === rawValue);
                            if (matched) {
                              return (
                                <Button
                                  key={matched.label}
                                  size="sm"
                                  bg={matched.color}
                                  color={getContrastColor(matched.color)}
                                  fontWeight="bold"
                                  cursor="default"
                                  _hover={{ opacity: 1 }}
                                >
                                  {matched.label}
                                </Button>
                              );
                            }
                            return rawValue;
                          }
                          if (type === "file") {
                            const meta = (
                              rawValue as unknown as {
                                file_metadata: {
                                  file_name: string;
                                  file_size: number;
                                  bucket: string;
                                  object_id: string;
                                };
                              }
                            )?.file_metadata;
                            if (!meta) return "-";

                            const fileName = meta.file_name;
                            const sizeBytes = meta.file_size;

                            const sizeFormatted =
                              sizeBytes >= 1024 * 1024
                                ? `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
                                : `${(sizeBytes / 1024).toFixed(1)} KB`;

                            const fileUrl = `${import.meta.env.VITE_MINIO_URL}/${meta.bucket}/${tableId}/${meta.object_id}`;

                            return (
                              <Link
                                href={fileUrl}
                                color="blue.500"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {fileName} ({sizeFormatted})
                              </Link>
                            );
                          }
                        } catch (error) {
                          console.error(
                            `Error processing field ${field.field_name}:`,
                            error
                          );
                          return String(rawValue); // fallback in case of parsing failure
                        }

                        return String(rawValue).slice(0, 200);
                      })()}
                    </Table.Cell>
                  ))}
                  <Table.Cell key={`created-${openSearchDoc.id}`}>
                    {(() => {
                      const metadata = openSearchDoc.data.metadata as Record<
                        string,
                        unknown
                      >;
                      const created_at = metadata?.created_at as string;
                      return created_at
                        ? new Date(created_at).toLocaleString()
                        : "-";
                    })()}
                  </Table.Cell>
                  <Table.Cell whiteSpace="nowrap">
                    {(() => {
                      const dataObj = openSearchDoc.data as Record<
                        string,
                        unknown
                      >;
                      const processState =
                        (dataObj &&
                          typeof dataObj === "object" &&
                          "metadata" in dataObj &&
                          (dataObj.metadata as Record<string, unknown>)
                            ?.process_state) ||
                        "IDLE";
                      return (
                        <ProcessStateTag
                          state={processState as string}
                          _disabled={{ opacity: 0.5 }}
                        />
                      );
                    })()}
                  </Table.Cell>
                  <Table.Cell>
                    <ActionsMenu
                      tableId={tableId}
                      openSearchDoc={openSearchDoc}
                    />
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      {fields.length > 0 && count > 0 && (
        <Flex justifyContent="flex-end" mt={4}>
          <PaginationRoot
            count={count}
            pageSize={PER_PAGE}
            onPageChange={({ page }) => setPage(page)}
          >
            <Flex>
              <PaginationPrevTrigger />
              <PaginationItems />
              <PaginationNextTrigger />
            </Flex>
          </PaginationRoot>
        </Flex>
      )}
    </>
  );
}

function TableRows() {
  const { tableId } = Route.useParams();

  // ✅ 빠른 필드 존재 여부 확인 (limit 1)
  const { data: fieldCheckData } = useQuery({
    queryKey: ["tableFieldsCheck", tableId],
    queryFn: () =>
      TableFieldsService.readTableFields({ tableId, skip: 0, limit: 1 }),
    enabled: !!tableId,
  });

  // 현재 테이블 조회
  const { data: tables } = useQuery({
    queryKey: ["tables"],
    queryFn: fetchTables,
  });

  const hasFields = (fieldCheckData?.data.length ?? 0) > 0;

  // 현재 테이블 ID에 해당하는 테이블 메타데이터 찾기
  const currentTable = tables?.data?.find((t) => t.id === tableId);

  return (
    <Container maxW="full">
      <Heading size="lg" pt={12}>
        {currentTable?.table_name ?? "Table"}
      </Heading>

      <Flex gap={4} py={4}>
        <AddTableRow tableId={tableId} disabled={!hasFields} />
        <AddTableRowFromSchema tableId={tableId} disabled={!hasFields} />
        <BulkUploadTableRows tableId={tableId} />
      </Flex>

      <TableDetail hasFields={hasFields} />
    </Container>
  );
}
